import { PortalDsField, portalDs } from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';
import type { CampaignEmailSectionsConfig } from '../types/campaign-email.types';
import {
  CAMPAIGN_EMAIL_AI_INSTRUCTION_PRESETS,
  CAMPAIGN_EMAIL_CHART_METRIC_OPTIONS,
  CAMPAIGN_EMAIL_METRIC_OPTIONS,
} from '../utils/campaign-email.constants';

interface CampaignEmailContentSectionProps {
  sectionsConfig: CampaignEmailSectionsConfig;
  aiNarrativeEnabled: boolean;
  customPromptInstructions: string;
  onSectionsConfigChange: (config: CampaignEmailSectionsConfig) => void;
  onAiNarrativeEnabledChange: (enabled: boolean) => void;
  onCustomPromptInstructionsChange: (value: string) => void;
  sectionsError?: string;
  customPromptError?: string;
  disabled?: boolean;
}

export function CampaignEmailContentSection({
  sectionsConfig,
  aiNarrativeEnabled,
  customPromptInstructions,
  onSectionsConfigChange,
  onAiNarrativeEnabledChange,
  onCustomPromptInstructionsChange,
  sectionsError,
  customPromptError,
  disabled = false,
}: CampaignEmailContentSectionProps) {
  const toggleMetric = (metric: string) => {
    const isSelected = sectionsConfig.summary_metrics.includes(metric);
    const summaryMetrics = isSelected
      ? sectionsConfig.summary_metrics.filter((value) => value !== metric)
      : [...sectionsConfig.summary_metrics, metric];

    onSectionsConfigChange({
      ...sectionsConfig,
      summary_metrics: summaryMetrics,
    });
  };

  const toggleChartMetric = (metric: string) => {
    const selected = sectionsConfig.chart_metrics ?? [];
    const isSelected = selected.includes(metric);
    let next: string[];
    if (isSelected) {
      next = selected.filter((value) => value !== metric);
    } else if (selected.length >= 2) {
      next = [...selected.slice(1), metric];
    } else {
      next = [...selected, metric];
    }
    if (next.length === 0) {
      next = ['spend'];
    }
    onSectionsConfigChange({
      ...sectionsConfig,
      chart_metrics: next,
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className={portalDs.headingH3}>
          Summary metrics
        </h3>
        <p className={cn(portalDs.caption, 'mt-1')}>
          Choose which metrics appear in the email summary cards.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {CAMPAIGN_EMAIL_METRIC_OPTIONS.map((option) => {
            const isSelected = sectionsConfig.summary_metrics.includes(option.value);
            return (
              <button
                key={option.value}
                type="button"
                disabled={disabled}
                aria-pressed={isSelected}
                onClick={() => toggleMetric(option.value)}
                className={cn(
                  'inline-flex items-center rounded-full border px-3 py-1.5 text-[13px] transition-colors',
                  isSelected
                    ? 'border-[#0D0F1A] bg-[#0D0F1A] text-white'
                    : 'border-[#EDEEF2] bg-white text-[#0D0F1A] hover:border-[#D1D5DB]',
                  disabled && 'cursor-not-allowed opacity-50',
                )}
              >
                {option.label}
              </button>
            );
          })}
        </div>
        {sectionsError ? (
          <p
            className="mt-2 text-xs text-[#DC2626]"
            role="alert"
          >
            {sectionsError}
          </p>
        ) : null}
      </div>

      <div className="space-y-4 rounded-lg border border-[#EDEEF2] p-4">
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={sectionsConfig.include_chart}
            disabled={disabled}
            onChange={(event) => onSectionsConfigChange({
              ...sectionsConfig,
              include_chart: event.target.checked,
            })}
            className="mt-1 size-4 rounded border-[#D1D5DB]"
          />
          <span>
            <span className="block text-[13px] font-medium text-[#0D0F1A]">
              Include performance chart
            </span>
            <span className={portalDs.caption}>
              Adds a daily trend chart to the email body.
            </span>
          </span>
        </label>

        {sectionsConfig.include_chart ? (
          <div>
            <p className="text-[13px] font-medium text-[#0D0F1A]">
              Chart metrics
            </p>
            <p className={cn(portalDs.caption, 'mt-1')}>
              Pick one or two metrics to plot. Two metrics use a dual-axis comparison.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {CAMPAIGN_EMAIL_CHART_METRIC_OPTIONS.map((option) => {
                const isSelected = (sectionsConfig.chart_metrics ?? []).includes(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    disabled={disabled}
                    aria-pressed={isSelected}
                    onClick={() => toggleChartMetric(option.value)}
                    className={cn(
                      'inline-flex items-center rounded-full border px-3 py-1.5 text-[13px] transition-colors',
                      isSelected
                        ? 'border-[#0D0F1A] bg-[#0D0F1A] text-white'
                        : 'border-[#EDEEF2] bg-white text-[#0D0F1A] hover:border-[#D1D5DB]',
                      disabled && 'cursor-not-allowed opacity-50',
                    )}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>

      <div className="space-y-4 rounded-lg border border-[#EDEEF2] p-4">
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={aiNarrativeEnabled}
            disabled={disabled}
            onChange={(event) => onAiNarrativeEnabledChange(event.target.checked)}
            className="mt-1 size-4 rounded border-[#D1D5DB]"
          />
          <span>
            <span className="block text-[13px] font-medium text-[#0D0F1A]">
              AI narrative
            </span>
            <span className={portalDs.caption}>
              Generate a written overview of campaign performance.
            </span>
          </span>
        </label>

        {aiNarrativeEnabled ? (
          <div className="space-y-3">
            <div>
              <p className="text-[13px] font-medium text-[#0D0F1A]">
                Quick instructions
              </p>
              <p className={cn(portalDs.caption, 'mt-1')}>
                Choose a common prompt to fill the instructions below. You can edit the text afterwards.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {CAMPAIGN_EMAIL_AI_INSTRUCTION_PRESETS.map((preset) => {
                  const isActive = customPromptInstructions === preset.instruction;
                  return (
                    <button
                      key={preset.id}
                      type="button"
                      disabled={disabled}
                      aria-pressed={isActive}
                      onClick={() => onCustomPromptInstructionsChange(preset.instruction)}
                      className={cn(
                        'inline-flex items-center rounded-full border px-3 py-1.5 text-[13px] transition-colors',
                        isActive
                          ? 'border-[#0D0F1A] bg-[#0D0F1A] text-white'
                          : 'border-[#EDEEF2] bg-white text-[#0D0F1A] hover:border-[#D1D5DB]',
                        disabled && 'cursor-not-allowed opacity-50'
                      )}
                    >
                      {preset.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <PortalDsField
              label="Custom instructions"
              hint="Optional guidance for the AI narrative."
              error={customPromptError}
            >
              <textarea
                className={cn(
                  portalDs.input,
                  'min-h-[120px] resize-y py-3'
                )}
                disabled={disabled}
                value={customPromptInstructions}
                placeholder="Keep it friendly and high-level — celebrate what’s working."
                onChange={(event) => onCustomPromptInstructionsChange(event.target.value)}
              />
            </PortalDsField>
          </div>
        ) : null}
      </div>
    </div>
  );
}
